import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * Generic themed modal for collecting 1-2 text inputs.
 * Replaces all browser prompt() calls.
 *
 * Props:
 *   isOpen, onClose,
 *   icon: LucideIcon component,
 *   title: string,
 *   fields: [{ key, label, placeholder, hint?, required? }],
 *   submitLabel: string,
 *   onSubmit: (values: Record<string, string>) => void
 */
export default function InputModal({ isOpen, onClose, icon: Icon, title, fields = [], submitLabel = "Add", onSubmit }) {
  const [values, setValues] = useState({});
  const firstRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const initial = {};
      fields.forEach((f) => { initial[f.key] = ""; });
      setValues(initial);
      setTimeout(() => firstRef.current?.focus(), 50);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const update = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  const canSubmit = fields.every((f) => !f.required || values[f.key]?.trim());

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(values);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
    if (e.key === "Escape") onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-bookish-purple/10 w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-bookish-purple/8 bg-bookish-cream/30">
          <div className="flex items-center gap-2">
            {Icon && <Icon size={18} className="text-bookish-purple" />}
            <h3 className="text-sm font-semibold text-bookish-purple">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-bookish-purple/8 text-bookish-purple/50 hover:text-bookish-purple transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {fields.map((field, i) => (
            <div key={field.key}>
              <label className="text-xs font-medium text-bookish-purple/70 block mb-1.5">
                {field.label}
                {!field.required && (
                  <span className="text-bookish-purple/40 font-normal ml-1">(optional)</span>
                )}
              </label>
              <input
                ref={i === 0 ? firstRef : undefined}
                type="text"
                value={values[field.key] || ""}
                onChange={(e) => update(field.key, e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={field.placeholder || ""}
                className="w-full border border-bookish-purple/15 rounded-xl px-3.5 py-2.5 text-sm bg-white focus:ring-2 focus:ring-bookish-purple/20 focus:border-bookish-purple/30 outline-none placeholder-bookish-purple/30"
              />
              {field.hint && (
                <p className="text-[11px] text-bookish-purple/40 mt-1">{field.hint}</p>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-bookish-purple/8 bg-bookish-cream/20">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-bookish-purple/60 rounded-lg hover:bg-bookish-purple/5 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-5 py-2 text-xs font-medium bg-bookish-purple text-white rounded-lg hover:bg-bookish-purpleAccent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
